import { Injectable } from '@angular/core';

import { TouchAndMouseMap } from '../../touch-and-mouse-map';
import { Select3dService } from '../../../services/select-3d.service';
import { ObjectControls } from '../../../services/object-controls.service';
import { CameraService } from '../../../services/camera.service';
import { SceneUtilsService } from '../../../services/scene-utils.service';
import { StageService } from '../../../services/stage.service';
import { Subscription } from 'rxjs';
import {
  IHandler,
  ClockwiseRotateHandler,
  CloneHandler,
  CounterClockwiseRotateHandler,
  DeleteHandler
} from './Handlers';
import { Drag3D } from '../Drag/drag3-d.service';

declare const THREE: any;

@Injectable()
export class AccessoryControlsService extends TouchAndMouseMap {
  _handlers: Array<IHandler> = [];
  _controls: any;
  _raycaster: any;
  _cameraPosition: any;
  _controlsRotation: any;
  _box: any;
  _selectObjSubscription: Subscription;
  _removeSubscription: Subscription;
  _clearSelectionSubscription: Subscription;
  dragUnsubscribe: () => void;
  handeled: boolean;

  constructor(
    private Select3D: Select3dService,
    private clockwiseRotateHandler: ClockwiseRotateHandler,
    private counterClockwiseRotateHandler: CounterClockwiseRotateHandler,
    private deleteHandler: DeleteHandler,
    private cloneHandler: CloneHandler,
    private objectControls: ObjectControls,
    private cameraManager: CameraService,
    private sceneUtils: SceneUtilsService,
    private stage: StageService,
    private drag3D: Drag3D
  ) {
    super();

    this._handlers = [
      this.clockwiseRotateHandler,
      this.counterClockwiseRotateHandler,
      this.deleteHandler,
      this.cloneHandler
    ];

    this._controls = objectControls.build();

    this._raycaster = new THREE.Raycaster();

    this._cameraPosition = null;

    this._controlsRotation = new THREE.Euler();
    this._box = new THREE.Box3();

    this.handeled = false;

    this._selectObjSubscription = this.Select3D.selectedAccessorySubject.subscribe(value => {
      stage.add(this._controls);
      this._manageButtonsVisibility();
      this._updatePosition();
      this._updateRotation();
    });

    this._removeSubscription = this.stage.objectRemoved.subscribe(_ =>
      this.objectControls.setVisibility(false)
    );
    this._clearSelectionSubscription = this.Select3D.clearSelected.subscribe(_ =>
      this.objectControls.setVisibility(false)
    );

    // this._selectObjUnsubscribe = Select3D.on('select', function (objs) {
    //     stage.add(this._controls);
    //     this._manageButtonsVisibility();
    //     this._updatePosition();
    //     this._updateRotation();
    // }.bind(this));

    this.dragUnsubscribe = this.drag3D.on('drag', this._updatePosition, this);

    // this._dragEndUnsubscribe = Drag3D.on('dragEnd', function () {
    //     this._updatePosition();
    // }.bind(this));
  }

  //wrapper around select 3d&2d
  // this._selectComponentProxy = new function (thisRef) {
  //     this.getSelected = function () {
  //         return this._getSelectedObjects();
  //     }.bind(thisRef);
  //     this.reset = function () {
  //         Selection2D.selectedObject = null;
  //         Select3D.clearSelection();
  //     };
  // }(this);

  hold(e: MouseEvent) {
    if (this.objectControls.isVisible()) {
      const ray = this._getRay(e.offsetX, e.offsetY);
      const button = this.objectControls.getButton(ray);

      if (button) {
        const handler = this._handlers.find(h => h.name === button) || null;

        if (handler) {
          handler.invoke(this.Select3D.getSelectedAccessories()[0]);
          this._manageButtonsVisibility();

          this.fireEvent('click', button);
          this.handeled = true;
          return true;
        }
      }
    }
  }

  move(e) {
    this._updateRotation();
  }

  release() {
    if (this.handeled) {
      this.handeled = false;
      return false;
    }
  }

  _updatePosition() {
    if (this.objectControls.isVisible()) {
      const selected = this.Select3D.getSelectedAccessories();
      const position = new THREE.Vector3();
      const positions = selected.map(obj => {
        obj.getWorldPosition(position);
        if (obj.userData.entity) position.y += obj.userData.entity.height / 2;
        return position.clone();
      });

      this._box.setFromPoints(positions);

      this._box.getCenter(position);

      if (this.cameraManager.isIn2D()) {
        var obj = selected[0];
        var entity = obj.userData.entity;
        var offset = new THREE.Vector3(entity.length / 2, 0, entity.width / 2)
          .applyAxisAngle(new THREE.Vector3(0, 1, 0), obj.rotation.y)
          .projectOnVector(new THREE.Vector3(0, 0, -1));
        position.add(new THREE.Vector3(0, 0, -1).multiplyScalar(offset.length()));
      }

      this.objectControls.setPosition(position);
    }
  }

  _updateRotation() {
    if (
      //keyState.isMouseHold &&
      this.objectControls.isVisible()
    ) {
      if (this.cameraManager.isIn2D()) {
        this._controlsRotation.set(-Math.PI / 2, 0, 0);
        this.objectControls.setRotation(this._controlsRotation);
      } else {
        this._cameraPosition = this.cameraManager.getCamera().position;
        this.objectControls.lookAtCamera(this._cameraPosition);
      }
    }
  }

  _manageButtonsVisibility() {
    const selected = this.Select3D.getSelectedAccessories();
    const handlers = this._handlers;
    let handler;
    let i = 0;

    if (selected.length === 0) {
      this.objectControls.setVisibility(false);
      return;
    }

    for (; i < handlers.length; i++) {
      handler = handlers[i];
      this.objectControls.setButtonVisibility(handler.name, handler.canExecute(selected));
    }

    this.objectControls.update();
  }

  _getRay(x, y) {
    this.sceneUtils.getRay(x, y, this._raycaster);
    if (this.cameraManager.isIn2D()) this._raycaster.ray.origin.y = 500;
    return this._raycaster;
  }

  disable() {
    super.disable();
    this.objectControls.setVisibility(false);
  }

  dispose() {
    super.dispose();
    this._selectObjSubscription.unsubscribe();
    this._removeSubscription.unsubscribe();
    this._clearSelectionSubscription.unsubscribe();
    // this._selectObjUnsubscribe();
    this.dragUnsubscribe();
    // this._dragEndUnsubscribe();
    // this._select2DUnsubscribe();

    // this._selectObjUnsubscribe = this._dragUnsubscribe =
    //     this._dragEndUnsubscribe = this._cameraPosition = null;
    this._selectObjSubscription = null;
  }
}



// WEBPACK FOOTER //
// ./src/app/Component(2D and 3D)/3D/ObjectControls/accessory-controls.service.ts